/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  type Boundary,
  flip,
  offset,
  shift,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

import { useIsInsideDialog } from '../dialog/dialog-context';

type UseComboboxPopoverOptions = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isDisabled: boolean;
  isPortaled?: boolean;
  floatingStrategy?: 'absolute' | 'fixed';
};

/**
 * Anchors the combobox panel to the control. Deliberately does not use
 * `useRole`, since the ARIA lives on the trigger button rather than on the
 * reference element, and the composition differs between the searchable and
 * select-only patterns.
 */
export function useComboboxPopover({
  isOpen,
  onOpenChange,
  isDisabled,
  isPortaled: isPortaledProp,
  floatingStrategy,
}: UseComboboxPopoverOptions) {
  const isInsideDialog = useIsInsideDialog();
  const isPortaled = isPortaledProp ?? !isInsideDialog;
  // Inside a Dialog the panel is fixed and not portaled. A transformed
  // ancestor can make Floating UI under-measure available space, so use the
  // viewport as the boundary instead.
  const floatingBoundary: Boundary = isInsideDialog ? [] : 'clippingAncestors';

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(4),
      flip({ fallbackPlacements: ['top-start'], padding: 5 }),
      shift({ boundary: floatingBoundary, padding: 5 }),
      sizeMiddleware({
        apply({ availableHeight, elements, rects }) {
          // The reference width is a floor the stylesheet combines with its own,
          // so a narrow trigger still gets a readable panel.
          elements.floating.style.setProperty(
            '--ndl-combobox-reference-width',
            `${rects.reference.width}px`,
          );
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
          });
        },
        boundary: floatingBoundary,
        padding: 5,
      }),
    ],
    onOpenChange: (open) => {
      onOpenChange(open);
    },
    open: !isDisabled && isOpen,
    placement: 'bottom-start',
    strategy: floatingStrategy ?? (isInsideDialog ? 'fixed' : 'absolute'),
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: !isDisabled });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return {
    context,
    floatingStyles,
    getFloatingProps,
    getReferenceProps,
    isPortaled,
    refs,
  };
}
